import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BancoldexService } from 'src/app/comun/services/bancoldex.service';
import { NeoComunService } from 'src/app/comun/services/neoComun.service';
import { VerificacionService } from 'src/app/comun/services/verificacion.service';
import { MicroempresarioService } from 'src/app/microempresario/services/microempresario.service';
import { ParametrosService } from 'src/app/services/parametros.service';
import { RouterService } from 'src/app/services/router.service';
import { SweetAlertService } from 'src/app/services/sweet-alert.service';

declare var $: any;

const mensajeEnvioCorreo = 'PAR-108';

/** Imágenes compuestas OTP registro (HU 8335) — `assets/img/verificacion/`. */
const OTP_IMAGE_NATURAL = 'assets/img/verificacion/otp-verificacion-natural.png';
const OTP_IMAGE_JURIDICA = 'assets/img/verificacion/otp-verificacion-juridica.png';
/** Auth (activar IF / desbloquear / otros): mismos assets que login. */
const OTP_AUTH_IF_DESKTOP = 'assets/img/login/login-if.png';
const OTP_AUTH_IF_MOBILE = 'assets/img/login/login-if-mobile.png';
const OTP_AUTH_MICRO_DESKTOP = 'assets/img/login/login-microempresario.png';
const OTP_AUTH_MICRO_MOBILE = 'assets/img/login/login-microempresario-mobile.png';

@Component({
  selector: 'app-verificacion-correo',
  templateUrl: './verificacion-correo.component.html',
  styleUrls: ['./verificacion-correo.component.scss'],
  standalone: false,
})
export class VerificacionCorreoComponent implements OnInit, OnDestroy {
  params: any = null;
  token = '';
  tokenNuevo = '';
  interval: ReturnType<typeof setInterval> | null = null;
  minutes: string | null = null;
  seconds: string | null = null;
  enviarForm: UntypedFormGroup;
  mensajeErrorOTP: string = null;
  formSubmitted = false;
  otpArray = new Array(5);
  otpValues: string[] = new Array(5).fill('');
  mensajeEnvioCorreo: string;

  constructor(
    private readonly _verificacion: VerificacionService,
    private readonly _microempresario: MicroempresarioService,
    private readonly _parametros: ParametrosService,
    private readonly _router: RouterService,
    private readonly route: Router,
    private readonly _sweetAlert: SweetAlertService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly _comun: NeoComunService,
    private readonly _bancoldexService: BancoldexService
  ) {
    window.scroll(0, 0);
    this.getMensajeEnvioCorreo();
    this.enviarForm = this.formBuilder.group({
      idUsuario: ['', Validators.required],
      asunto: [1, Validators.required],
      mensajeUsuario: ['', Validators.required],
      correoElectronico: [''],
    });
    this.getMensajeError();
  }

  ngOnInit(): void {
    this.params = this._verificacion.getParameters() || {};
    this.hydrateDesbloqueoFromSession();
    this.hydrateTipoPersonaFromSession();
    this.timer();
  }

  /**
   * HU 8337 / 8335: el OTP de desbloqueo NO debe caer en landing de bienvenida.
   * Si hay refresh, recupera el flag isDesbloquear desde sessionStorage.
   */
  private hydrateDesbloqueoFromSession(): void {
    if (this.params?.isDesbloquear) {
      return;
    }
    try {
      const raw = sessionStorage.getItem('verificacion-desbloqueo');
      if (!raw) {
        return;
      }
      const unlock = JSON.parse(raw);
      if (!unlock?.isDesbloquear) {
        return;
      }
      this.params = { ...this.params, ...unlock, isDesbloquear: true };
      this._verificacion.saveParameters(this.params);
    } catch {
      // ignore
    }
  }

  private clearDesbloqueoSession(): void {
    try {
      sessionStorage.removeItem('verificacion-desbloqueo');
    } catch {
      // ignore
    }
  }

  /** Si no viene en params (p. ej. refresh), intenta recuperarlo del pre-registro. */
  private hydrateTipoPersonaFromSession(): void {
    try {
      const raw = sessionStorage.getItem('pre-registro');
      if (!raw) {
        return;
      }
      const pre = JSON.parse(raw);
      const next: Record<string, unknown> = { ...this.params };
      if (!this.params?.tipoPersona) {
        if (pre?.tipoPersona) {
          next['tipoPersona'] = pre.tipoPersona;
        } else if (pre?.idPersonaEmpJuridico != null) {
          next['tipoPersona'] = 'juridica';
        }
      }
      if (!this.params?.primerNombre && pre?.primerNombre) {
        next['primerNombre'] = pre.primerNombre;
      }
      if (!this.params?.primerApellido && pre?.primerApellido) {
        next['primerApellido'] = pre.primerApellido;
      }
      this.params = next;
    } catch {
      // ignore
    }
  }

  ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  get isJuridica(): boolean {
    const tipo = (this.params?.tipoPersona || this.params?.perfil || '')
      .toString()
      .toLowerCase();
    return tipo === 'juridica' || tipo === 'juridico' || tipo === 'pj';
  }

  /**
   * OTP de registro PN/PJ (HU 8335): layout e imágenes actuales.
   * Todo lo demás usa skin auth (panel + imagen login).
   */
  get isRegistroOtp(): boolean {
    if (this.params?.isInter || this.params?.isDesbloquear) {
      return false;
    }
    const tipo = (this.params?.tipoPersona || this.params?.perfil || '')
      .toString()
      .toLowerCase()
      .trim();
    if (tipo === 'natural' || tipo === 'juridica' || tipo === 'juridico' || tipo === 'pj' || tipo === 'pn') {
      return true;
    }
    try {
      const raw = sessionStorage.getItem('pre-registro');
      if (raw) {
        const pre = JSON.parse(raw);
        if (pre?.tipoPersona || pre?.idPersonaEmpJuridico != null || pre?.primerNombre) {
          return true;
        }
      }
    } catch {
      // ignore
    }
    return false;
  }

  get isAuthOtp(): boolean {
    return !this.isRegistroOtp;
  }

  /** Activación aliado: imagen login IF. Resto auth: imagen login micro (default). */
  get isAuthIf(): boolean {
    return !!this.params?.isInter;
  }

  get otpImageSrc(): string {
    if (this.isRegistroOtp) {
      return this.isJuridica ? OTP_IMAGE_JURIDICA : OTP_IMAGE_NATURAL;
    }
    return this.isAuthIf ? OTP_AUTH_IF_DESKTOP : OTP_AUTH_MICRO_DESKTOP;
  }

  get otpImageMobileSrc(): string {
    if (this.isRegistroOtp) {
      return this.otpImageSrc;
    }
    return this.isAuthIf ? OTP_AUTH_IF_MOBILE : OTP_AUTH_MICRO_MOBILE;
  }

  get displayEmail(): string {
    return this.params?.email || 'correo@mail.com';
  }

  onInputChange(event: Event, index: number): void {
    const inputElement = event.target as HTMLInputElement;
    const nextInput = inputElement.nextElementSibling as HTMLInputElement;
    if (!/^\d$/.test(inputElement.value)) {
      inputElement.value = '';
      this.otpValues[index] = '';
      return;
    }
    this.otpValues[index] = inputElement.value;
    if (nextInput && inputElement.value !== '') {
      nextInput.focus();
    }
    if (index === this.otpValues.length - 1 && inputElement.value) {
      this.verificarCod();
    }
  }

  onBackspace(event: Event, index: number): void {
    const keyboardEvent = event as KeyboardEvent;
    const inputElement = keyboardEvent.target as HTMLInputElement;
    const previousInput = inputElement.previousElementSibling as HTMLInputElement;
    if (keyboardEvent.key === 'Backspace' && inputElement.value === '' && previousInput) {
      previousInput.focus();
    }
  }

  onPaste(event: ClipboardEvent, index: number): void {
    event.preventDefault();
    const data = (event.clipboardData || (window as any).clipboardData)
      .getData('text')
      .replace(/\D/g, '');

    if (!data) {
      return;
    }

    const inputs = Array.from(
      (event.currentTarget as HTMLElement).parentElement!.querySelectorAll<HTMLInputElement>(
        'input.verificacion-otp__input'
      )
    );
    let i = index;
    for (const ch of data) {
      if (i >= inputs.length) {
        break;
      }
      this.otpValues[i] = ch;
      i++;
    }
    const focusIndex = Math.min(i, inputs.length - 1);
    inputs[focusIndex].focus();
    if (this.otpValues.every((v) => v !== '')) {
      this.verificarCod();
    }
  }

  verificarCod(): void {
    this.formSubmitted = true;
    if (this.otpValues.includes('')) {
      this._sweetAlert.errorModal('¡Error!', true, 'Ingresa todos los dígitos del código OTP.');
      return;
    }

    const code = this.otpValues.join('');
    if (this.interval) {
      clearInterval(this.interval);
    }

    if (this.params?.isInter) {
      const body = {
        token: this.params.token,
        codigo: code,
        password: this.params.password,
        correoElectronico: this.params.email,
      };
      this._parametros.activarUsuario(body).subscribe((data: any) => {
        if (data.exitoso == 'true' || data.exitoso == true) {
          this.route.navigateByUrl('/LandingAceptacionAliado');
          localStorage.removeItem('activate');
        } else {
          this._sweetAlert.errorModal(this.mensajeErrorOTP, true);
        }
      });
    } else if (this.params?.isDesbloquear) {
      // Desbloqueo: éxito → home/login. Nunca LandingAceptacionUsuario.
      const json = {
        token: this.params.token,
        codigo: code,
        idUsuario: this.params.idUsuario,
      };
      this._parametros.validarDesbloqueoUsuario(json).subscribe((data: any) => {
        if (data.exitoso == 'true' || data.exitoso == true) {
          this.clearDesbloqueoSession();
          this._sweetAlert
            .successModal(
              'Usuario desbloqueado',
              true,
              'Validación exitosa de identidad y código digitado. Ahora puedes realizar el proceso de restablecimiento de contraseña o inicio de sesión en el canal correctamente.',
              'Restablece tu contraseña',
              'Iniciar sesión'
            )
            .then((result) => {
              if (result.isConfirmed) {
                const body = {
                  correoElectronico: this.params.email,
                  captchaToken: this.params.captchaToken,
                };
                this._parametros.sendMailRecordar(body).subscribe((mailData: any) => {
                  if (mailData.exitoso == 'true' || mailData.exitoso == true) {
                    this._sweetAlert
                      .infoModal(
                        mailData.correoOculto + ' enviado exitosamente',
                        false,
                        this.mensajeEnvioCorreo
                      )
                      .then((infoResult) => {
                        if (infoResult.isConfirmed) {
                          this.route.navigateByUrl('/home');
                        }
                      });
                  }
                });
              } else if (result.dismiss) {
                this.route.navigateByUrl('login');
              }
            });
        } else {
          this._sweetAlert.errorModal(this.mensajeErrorOTP, true);
        }
      });
    } else {
      this._microempresario.validateCod({ codigo: code, ...this.params }).subscribe((data: any) => {
        if (data.exitoso == 'true' || data.exitoso == true) {
          this.clearDesbloqueoSession();
          this.persistLandingBienvenida();
          this.route.navigateByUrl('/LandingAceptacionUsuario');
          sessionStorage.removeItem('pre-registro');
          localStorage.removeItem('pre-registro');
        } else {
          this._sweetAlert.errorModal(this.mensajeErrorOTP, true);
        }
      });
    }
  }

  /** Datos para saludo personalizado + precarga login (HU 8335 / 8337). */
  private persistLandingBienvenida(): void {
    let primerNombre = this.params?.primerNombre;
    let primerApellido = this.params?.primerApellido;
    let tipoPersona = this.params?.tipoPersona;
    let tipoDocumento = this.params?.tipoDocumento ?? this.params?.idTipoDocIdentidad;
    let numeroDocumento = this.params?.numeroDocumento;
    try {
      const raw = sessionStorage.getItem('pre-registro');
      if (raw) {
        const pre = JSON.parse(raw);
        primerNombre = primerNombre || pre?.primerNombre;
        primerApellido = primerApellido || pre?.primerApellido;
        tipoPersona = tipoPersona || pre?.tipoPersona;
        tipoDocumento =
          tipoDocumento ?? pre?.tipoDocumento ?? pre?.idTipoDocIdentidad;
        numeroDocumento = numeroDocumento || pre?.numeroDocumento;
      }
    } catch {
      // ignore
    }
    sessionStorage.setItem(
      'landing-bienvenida',
      JSON.stringify({
        primerNombre: primerNombre || '',
        primerApellido: primerApellido || '',
        tipoPersona: tipoPersona || 'natural',
        tipoDocumento: tipoDocumento ?? '',
        numeroDocumento: numeroDocumento || '',
      })
    );
  }

  sendOtp(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
    if (!this.params?.email) {
      this._sweetAlert.errorModal('Error!', true, 'No hay correo asociado para reenviar el código.');
      return;
    }
    this._parametros.sendOtp(this.params.email).subscribe((data: any) => {
      if (data.exitoso == 'true' || data.exitoso == true) {
        this._sweetAlert.successModal('Código enviado');
        this.timer();
      } else {
        this._sweetAlert.errorModal(data.error, true);
      }
    });
  }

  back(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
    this._router.goBack();
  }

  timer(): void {
    let date = new Date('2023-01-01 00:03');
    const padLeft = (n: string) => '00'.substring(0, '00'.length - n.length) + n;
    this.interval = setInterval(() => {
      this.minutes = padLeft(date.getMinutes() + '');
      this.seconds = padLeft(date.getSeconds() + '');
      date = new Date(date.getTime() - 1000);
      if (this.minutes == '00' && this.seconds == '00') {
        if (this.interval) {
          clearInterval(this.interval);
        }
        this._sweetAlert.infoModal(
          'El código ya NO es válido para ingresar, porque se venció el tiempo, solícita uno nuevo.'
        );
      }
    }, 1000);
  }

  help(): void {
    $('#correoModal').modal('show');
  }

  isValidForm(field: string): boolean {
    return (
      (this.enviarForm.get(field)?.dirty || this.enviarForm.get(field)?.touched) &&
      this.enviarForm.get(field)?.invalid
    );
  }

  enviarCorreo(): void {
    this.enviarForm.get('idUsuario').setValue(+this.params.idUsuario);
    this.enviarForm.get('correoElectronico').setValue(this.params.email);
    if (!this.enviarForm.invalid) {
      this._comun.enviarCorreo(this.enviarForm.value).subscribe((resp: any) => {
        if (resp.exitoso) {
          $('#correoModal').modal('hide');
          this._sweetAlert.successModal(
            'Hemos enviado la información a nuestro equipo de soporte. ¡Nos contactaremos contigo en el menor tiempo posible para dar solución a tu solicitud!'
          );
          this.enviarForm.reset();
        } else {
          this._sweetAlert.errorModal(
            'Se ha presentado un error al momento de enviar el correo.',
            true
          );
        }
      });
    } else {
      this.enviarForm.markAllAsTouched();
    }
  }

  reset(): void {
    this.enviarForm.reset();
  }

  getMensajeError(): void {
    const json = {
      idParametro: 'PAR-134',
      nombre: null,
      isParFuncional: true,
      pagina: 1,
    };
    this._bancoldexService.getFilterParametro(json).subscribe(
      (response) => {
        const respuesta = response['data'];
        this.mensajeErrorOTP = respuesta[0].valorParametro;
      },
      (error) => {
        this._sweetAlert.errorModal(error.error, true);
      }
    );
  }

  getMensajeEnvioCorreo(): void {
    const json = {
      idParametro: mensajeEnvioCorreo,
      nombre: null,
      isParFuncional: true,
      pagina: 1,
    };
    this._bancoldexService.getFilterParametro(json).subscribe(
      (response) => {
        const respuesta = response['data'];
        this.mensajeEnvioCorreo = respuesta[0].valorParametro;
      },
      (error) => {
        this._sweetAlert.errorModal('Error!', true, error.error);
      }
    );
  }
}
